import React, { useEffect, useState } from 'react';

import AlertDialog from '../shared/components/AlertDialog';
import { ApiPortfolioState } from '../dashboard/models/ApiPortfolioState';
import AuthService from '../shared/services/AuthService';
import { ExternalLessonContextProvider } from '../dashboard/models/ExternalLessonContext';
import { IExternalToolComponentProps } from './ExternalTool';
import LessonPlayer from '../content/components/LessonPlayer/LessonPlayer';
import { ThemeProvider } from '@mui/material/styles';
import useLessonColorTheme from '../shared/hooks/useLessonColorTheme';
import useLinkLesson from '../content/hooks/useLinkLesson';
import { useParams } from 'react-router-dom';

/** Properties passed to the shared lesson link component. */
export interface IExternalLinkLessonProps extends IExternalToolComponentProps {}

/**
 * Component to render a lesson in read-only mode from a sharing link.
 */
const ExternalLinkLesson = (props: IExternalLinkLessonProps) => {
  const { linkkey } = useParams<{ linkkey: string }>();
  const { loading, error, lesson, components } = useLinkLesson(linkkey);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const { lessonColorTheme } = useLessonColorTheme(
    lesson?.displaySettings?.palette
  );

  useEffect(() => {
    AuthService.setApiKey(linkkey);
  }, [linkkey]);

  useEffect(() => {
    props.onContentLoadingMessage(loading && `Loading lesson...`);
  }, [loading]);

  useEffect(() => {
    props.onContentErrorMessage(error);
  }, [error]);

  return (
    <ThemeProvider theme={lessonColorTheme}>
      <ExternalLessonContextProvider value={{ isExternalLesson: true }}>
        {lesson && components && (
          <LessonPlayer
            open
            loading={loading}
            displayMode="preview"
            lessonkey={lesson.lessonkey}
            lessonTitle={lesson.title}
            lessonDescription={lesson.description}
            lessonType={lesson.type}
            displaySettings={lesson.displaySettings}
            components={components}
            state={ApiPortfolioState.Unknown}
            completionType={lesson.behaviorSettings.completionType}
            completionThreshold={lesson.behaviorSettings.completionThreshold}
            onClose={() => setShowExitDialog(true)}
          />
        )}
        {showExitDialog && (
          <AlertDialog
            title="Exit lesson"
            message="Close the browser window to exit this lesson."
            onClose={() => setShowExitDialog(false)}
          />
        )}
      </ExternalLessonContextProvider>
    </ThemeProvider>
  );
};

export default ExternalLinkLesson;
